import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Grid, Stack, Chip, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';
import { getInquiryDetail } from '../../api/endpoints/astrologers';
import { formatDateTime } from '../../utils/formatters';

const statusColor = { new: 'info', contacted: 'warning', closed: 'default' };

const AstrologerInquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getInquiryDetail(id);
        setInquiry(res.data.data);
      } catch {
        toast.error('Inquiry not found');
        navigate('/astrologer-inquiries');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!inquiry) return null;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/astrologer-inquiries')} sx={{ mb: 2 }}>Back to Inquiries</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>Inquiry Detail</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={600}>{inquiry.name}</Typography>
                  <Chip label={inquiry.status} color={statusColor[inquiry.status] || 'default'} />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <a href={`mailto:${inquiry.email}`} style={{ textDecoration: 'none' }}>
                        <Typography variant="body2" color="primary">{inquiry.email}</Typography>
                      </a>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Mobile</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocalPhoneIcon fontSize="small" color="action" />
                      <a href={`tel:${inquiry.mobile}`} style={{ textDecoration: 'none' }}>
                        <Typography variant="body2" color="primary">{inquiry.mobile}</Typography>
                      </a>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Submitted On</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarTodayIcon fontSize="small" color="action" />
                      <Typography variant="body2">{formatDateTime(inquiry.createdAt)}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                {inquiry.note && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Notes / Other Details</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, bgcolor: '#f5f5f5', p: 2, borderRadius: 1, whiteSpace: 'pre-wrap' }}>{inquiry.note}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Quick Actions</Typography>
              <Stack spacing={1.5}>
                <Button variant="outlined" fullWidth startIcon={<LocalPhoneIcon />} href={`tel:${inquiry.mobile}`} size="small">
                  Call {inquiry.mobile}
                </Button>
                <Button variant="outlined" fullWidth startIcon={<EmailIcon />} href={`mailto:${inquiry.email}`} size="small">
                  Email {inquiry.email}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AstrologerInquiryDetail;
